import { Button, Chip } from '@emailforge/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { TopBar } from '@/components/layout/TopBar';
import { Icon } from '@/components/ui/icons';
import { useCreateTemplateMutation, useListGalleryQuery } from '@/features/templates/templatesApi';

export function GalleryScreen() {
  const navigate = useNavigate();
  const { data, isLoading } = useListGalleryQuery();
  const [createTemplate, { isLoading: isForking }] = useCreateTemplateMutation();
  const [activeCategory, setActiveCategory] = useState<string>('all');

  const categories = Array.from(new Set((data ?? []).map((g) => g.category)));
  const list = (data ?? []).filter((g) => activeCategory === 'all' || g.category === activeCategory);

  const handleFork = async (id: string, name: string) => {
    const res = await createTemplate({ name, forkFromGalleryId: id }).unwrap();
    navigate(`/app/templates/${res.id}`);
  };

  return (
    <>
      <TopBar
        crumbs={['Gallery']}
        right={
          <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            {(data ?? []).length} starter templates
          </div>
        }
      />
      <div style={{ flex: 1, padding: 24, overflowY: 'auto' }}>
        <div style={{ maxWidth: 960, margin: '0 auto 24px' }}>
          <h1 className="display" style={{ fontSize: 32, margin: '0 0 8px', letterSpacing: -0.015 }}>
            Starter templates
          </h1>
          <p style={{ color: 'var(--ink-2)', fontSize: 14, margin: '0 0 20px' }}>
            Fork any of these templates into your workspace. You keep full ownership — no lock-in.
          </p>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <CategoryChip label="All" active={activeCategory === 'all'} onClick={() => setActiveCategory('all')} />
            {categories.map((c) => (
              <CategoryChip
                key={c}
                label={c}
                active={activeCategory === c}
                onClick={() => setActiveCategory(c)}
              />
            ))}
          </div>
        </div>

        {isLoading && (
          <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', textAlign: 'center' }}>
            Loading gallery…
          </div>
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 16,
            maxWidth: 1200,
            margin: '0 auto',
          }}
        >
          {list.map((g) => (
            <div key={g.id} className="mf-card" style={{ padding: 0, overflow: 'hidden' }}>
              <GalleryThumbnail slug={g.slug} category={g.category} name={g.name} />
              <div style={{ padding: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <div style={{ fontSize: 14, fontWeight: 550, flex: 1 }}>{g.name}</div>
                  {g.forkedCount > 0 && (
                    <Chip variant="flame" dot={false}>
                      <Icon.flame size={10} /> {g.forkedCount} forks
                    </Chip>
                  )}
                </div>
                <div
                  className="mono"
                  style={{
                    fontSize: 10.5,
                    color: 'var(--ink-3)',
                    textTransform: 'uppercase',
                    letterSpacing: 0.06,
                    marginBottom: 12,
                  }}
                >
                  {g.category}
                </div>
                <Button variant="flame" size="sm" style={{ width: '100%', justifyContent: 'center' }}
                  onClick={() => handleFork(g.id, g.name)}
                  disabled={isForking}
                >
                  Fork to workspace
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/**
 * Renders the gallery template's actual server-rendered HTML inside a scaled
 * iframe — replaces the gradient placeholder so users see what they're
 * actually forking. Falls back to the gradient on iframe load error (e.g.
 * the gallery row's slug doesn't have a render endpoint yet).
 */
function GalleryThumbnail({
  slug,
  category,
  name,
}: {
  slug: string;
  category: string;
  name: string;
}) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div
        style={{
          height: 180,
          background: `linear-gradient(135deg, ${colorForCategory(category)}, oklch(0.50 0.20 15))`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontFamily: 'var(--font-display)',
          fontSize: 24,
          letterSpacing: -0.01,
          padding: 20,
          textAlign: 'center',
        }}
      >
        {name}
      </div>
    );
  }
  return (
    <div
      style={{
        height: 220,
        overflow: 'hidden',
        background: '#f6f3ee',
        position: 'relative',
        borderBottom: '1px solid var(--line-soft)',
      }}
    >
      {/* The render is 600px wide; we scale to ~38% so it fits a 260px tile.
          `pointer-events: none` so users can't click into the iframe — the
          card itself owns the click target via the Fork button below. */}
      <iframe
        title={`${name} preview`}
        src={`/api/gallery/templates/${encodeURIComponent(slug)}/preview.html`}
        sandbox="allow-same-origin"
        loading="lazy"
        onError={() => setFailed(true)}
        style={{
          width: 600,
          height: 580,
          border: 0,
          transform: 'scale(0.38)',
          transformOrigin: 'top left',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}

function CategoryChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <Button size="sm" onClick={onClick}
      style={{
        background: active ? 'var(--ink)' : 'var(--paper)',
        color: active ? 'var(--paper)' : 'var(--ink-2)',
        borderColor: active ? 'var(--ink)' : 'var(--line)',
        textTransform: 'capitalize',
      }}>
      {label}
    </Button>
  );
}

function colorForCategory(category: string): string {
  const map: Record<string, string> = {
    transactional: 'oklch(0.72 0.14 32)',
    lifecycle: 'oklch(0.65 0.14 232)',
    marketing: 'oklch(0.72 0.15 148)',
    product: 'oklch(0.70 0.14 85)',
  };
  return map[category] ?? 'oklch(0.72 0.10 45)';
}
